import React, { useState, useRef } from "react";
import WindowHeader from "../../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../../assets/images/shersoftnavLogo.png";
import GeneralSettings from "../GeneralSettings/GeneralSettings";
import CompanySettings from "../CompanySettings/CompanySettings";
import PrinterSettings from "../PrinterSettings/PrinterSettings";
import GatewaySettings from "../GatewaySettings/GatewaySettings";
import {
  FiSliders,
  FiCheckCircle,
  FiRefreshCw,
  FiSave,
  FiMapPin,
  FiInfo,
  FiCheck,
} from "react-icons/fi";

function SoftWareSettings({
  onClose,
  zIndex,
  openSmsSettings,
  openwhatsAppSettings,
}) {
  const [activeTab, setActiveTab] = useState("General Settings");
  const [syncActive, setSyncActive] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // References for active tab save / refresh delegates
  const companySettingsRef = useRef(null);
  const generalSettingsRef = useRef(null);
  const printerSettingsRef = useRef(null);

  const handleRefresh = () => {
    setIsRefreshing(true);
    if (activeTab === "Company Settings") {
      companySettingsRef.current?.fetchCompanyDetails?.();
    } else if (activeTab === "General Settings") {
      generalSettingsRef.current?.refreshSettings?.();
    } else if (activeTab === "Printer Settings") {
      printerSettingsRef.current?.fetchSettings?.();
    }
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const triggerChildSave = () => {
    if (activeTab === "Company Settings") {
      companySettingsRef.current?.handleSave?.();
    } else if (activeTab === "General Settings") {
      generalSettingsRef.current?.handleSave?.();
    } else if (activeTab === "Printer Settings") {
      printerSettingsRef.current?.handleSave?.();
    }
  };

  // Drag-to-scroll state for tabs
  const tabContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    if (tabContainerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - tabContainerRef.current.offsetLeft);
      setScrollLeft(tabContainerRef.current.scrollLeft);
      tabContainerRef.current.style.cursor = "grabbing";
    }
  };

  const handleMouseLeave = () => {
    if (tabContainerRef.current) {
      setIsDragging(false);
      tabContainerRef.current.style.cursor = "grab";
    }
  };

  const handleMouseUp = () => {
    if (tabContainerRef.current) {
      setIsDragging(false);
      tabContainerRef.current.style.cursor = "grab";
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    if (tabContainerRef.current) {
      const x = e.pageX - tabContainerRef.current.offsetLeft;
      const walk = (x - startX) * 2;
      tabContainerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  // Exact 6 tabs matching design
  const tabs = [
    {
      id: "Company Settings",
      label: "Company Settings",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <line x1="9" y1="6" x2="9" y2="6.01" />
          <line x1="15" y1="6" x2="15" y2="6.01" />
          <line x1="9" y1="10" x2="9" y2="10.01" />
          <line x1="15" y1="10" x2="15" y2="10.01" />
          <line x1="9" y1="14" x2="9" y2="14.01" />
          <line x1="15" y1="14" x2="15" y2="14.01" />
          <path d="M9 18h6v4H9z" />
        </svg>
      ),
    },
    {
      id: "Printer Settings",
      label: "Printer Settings",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 6 2 18 2 18 9" />
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
          <rect x="6" y="14" width="12" height="8" />
        </svg>
      ),
    },
    {
      id: "Report Settings",
      label: "Report Settings",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
          <path d="M22 12A10 10 0 0 0 12 2v10z" />
        </svg>
      ),
    },
    {
      id: "General Settings",
      label: "General Settings",
      badge: "8",
      badgeClass: "bg-amber-100 text-amber-700",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      ),
    },
    {
      id: "Gateway Settings",
      label: "Gateway Settings",
      badge: "2",
      badgeClass: "bg-emerald-100 text-emerald-700",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    },
    {
      id: "Custom Settings",
      label: "Custom Settings",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m15 4 4 4" />
          <path d="m20 9-8.5 8.5a2.12 2.12 0 0 1-3-3L17 6" />
          <path d="M3 21l3-3" />
          <path d="M14 2l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
        </svg>
      ),
    },
    {
      id: "Backup Settings",
      label: "Backup Settings",
      badge: "2",
      badgeClass: "bg-blue-100 text-blue-700",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
      ),
    },
  ];

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto select-none font-['Inter',sans-serif]"
      style={{ zIndex: zIndex || 101 }}
    >
      {/* ── Main Modal Container (Exact layout from CustomerAppReg) ── */}
      <div className="bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[900px] border border-slate-200 shadow-md flex flex-col overflow-hidden absolute">
        
        {/* ── Window Header ── */}
        <WindowHeader
          title="Software Settings Master"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f2d59] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* ── Action Toolbar Panel / Mini-Header info stats ── */}
        <div className="bg-white border-b border-slate-200/80 px-4 py-2 shrink-0 flex flex-nowrap items-center justify-between gap-2.5 overflow-x-auto">
          {/* Left Title & Icon */}
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-11 h-11 bg-[#0d2347] rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
              <FiSliders className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-[17px] font-bold text-slate-800 leading-tight">Software Settings Master</h1>
              <p className="text-[11.5px] text-slate-500 font-medium">Configure workstation policies, receipt schemes, bullion rates display &amp; accounting defaults</p>
            </div>
          </div>

          {/* Counters row & Action Buttons */}
          <div className="flex items-center gap-2.5 ml-auto shrink-0 flex-nowrap">
            {/* Showroom Location Card */}
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200/80 shrink-0">
              <div className="w-7.5 h-7.5 rounded-full bg-blue-100/50 text-[#0f2d59] flex items-center justify-center shrink-0">
                <FiMapPin className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div className="leading-tight">
                <span className="text-[9.5px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Showroom Location</span>
                <span className="text-[13px] font-extrabold text-slate-700">SHOP (Loc #1)</span>
              </div>
            </div>

            {/* Policy Status Card */}
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200/80 shrink-0">
              <div className="w-7.5 h-7.5 rounded-full bg-emerald-100/50 text-emerald-600 flex items-center justify-center shrink-0">
                <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div className="leading-tight">
                <span className="text-[9.5px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Policy Status</span>
                <span className="text-[13px] font-extrabold text-emerald-600">ACTIVE</span>
              </div>
            </div>

            {/* Settings Sync Toggle */}
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200/80 shrink-0">
              <span className="text-[11.5px] font-semibold text-slate-700">Settings Sync</span>
              <button
                type="button"
                onClick={() => setSyncActive(!syncActive)}
                className={`w-8 h-4 rounded-full relative p-0.5 transition-colors duration-200 focus:outline-none ${
                  syncActive ? "bg-emerald-500" : "bg-slate-300"
                }`}
                role="switch"
                aria-checked={syncActive}
              >
                <span
                  className={`block w-3 h-3 bg-white rounded-full shadow transform transition-transform duration-200 ${
                    syncActive ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Header buttons */}
            <div className="flex items-center gap-2 ml-2 shrink-0">
              <button
                type="button"
                onClick={handleRefresh}
                className="h-8 px-3 border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-lg text-xs flex items-center gap-1.5 transition-colors cursor-pointer bg-white"
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                <span>Refresh</span>
              </button>
              <button
                type="button"
                onClick={triggerChildSave}
                className="h-8 px-3.5 bg-[#b88628] hover:bg-[#a67722] text-white font-bold rounded-lg text-xs flex items-center gap-1.5 transition-colors cursor-pointer border-none shadow-sm"
              >
                <FiSave className="w-3.5 h-3.5" />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Modern Tabs Navigation Bar ── */}
        <div className="bg-white px-4 border-b border-slate-200/80 select-none shrink-0">
          <nav
            ref={tabContainerRef}
            className="flex space-x-6 overflow-x-auto text-[12px] font-semibold scrollbar-none cursor-grab py-0"
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2.5 bg-transparent hover:bg-transparent focus:outline-none shadow-none transition flex items-center space-x-1.5 border-b-2 border-t-0 border-l-0 border-r-0 whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "text-blue-600 border-blue-600 font-bold"
                      : "text-slate-600 hover:text-slate-900 border-transparent font-medium"
                  }`}
                >
                  <span className={isActive ? "text-blue-600" : "text-slate-400"}>
                    {tab.icon}
                  </span>
                  <span>{tab.label}</span>
                  {tab.badge && (
                    <span
                      className={`ml-1 text-[9.5px] font-bold px-1.5 py-0.2 rounded-full ${tab.badgeClass}`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* ── Main Work Area ── */}
        <div className="flex-1 p-3 bg-slate-50 overflow-y-auto min-h-0 font-['Inter',sans-serif]">
          {activeTab === "Company Settings" && (
            <CompanySettings ref={companySettingsRef} onClose={onClose} />
          )}
          {activeTab === "Printer Settings" && (
            <PrinterSettings ref={printerSettingsRef} onClose={onClose} />
          )}
          {activeTab === "General Settings" && (
            <GeneralSettings
              ref={generalSettingsRef}
              openWhatsAppSettings={openwhatsAppSettings}
              openSmsSettings={openSmsSettings}
              onClose={onClose}
            />
          )}
          {activeTab === "Gateway Settings" && (
            <GatewaySettings
              openSmsSettings={openSmsSettings}
              openWhatsAppSettings={openwhatsAppSettings}
              onClose={onClose}
            />
          )}
          {activeTab !== "Company Settings" &&
            activeTab !== "Printer Settings" &&
            activeTab !== "General Settings" &&
            activeTab !== "Gateway Settings" && (
              <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm flex flex-col items-center justify-center text-center space-y-2 min-h-[260px]">
                <div className="w-12 h-12 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 text-xl">
                  <FiSliders className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-bold text-slate-800">{activeTab}</h3>
                <p className="text-xs text-slate-500 max-w-md">
                  Configuration parameters for {activeTab.toLowerCase()} are managed automatically based on system workstation defaults.
                </p>
              </div>
            )}
        </div>

        {/* ── Footer Status & Commit Bar ── */}
        <div className="bg-white border-t border-slate-200/80 px-4 py-2 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs shrink-0 select-none">
          <div className="flex items-center space-x-1.5 text-slate-500 text-[11px]">
            <FiInfo className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span>
              Changes applied here update <strong>system-wide configuration</strong> across all local terminals in <strong>SHOP</strong>.
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 px-3 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 text-[11px] transition shadow-xs cursor-pointer"
            >
              Discard Changes
            </button>
            <button
              type="button"
              onClick={triggerChildSave}
              className="h-8 px-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold rounded-lg text-[11px] shadow-sm transition flex items-center space-x-1.5 border border-amber-600 cursor-pointer"
            >
              <FiCheck className="w-3.5 h-3.5" />
              <span>Save &amp; Apply Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SoftWareSettings;
